
import React, { useState, useRef, useEffect } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (category: any, content: string) => Promise<void>;
}

const FeedbackModal: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const [category, setCategory] = useState<'Suggestion' | 'Bug' | 'Content Request' | 'Other'>('Suggestion');
  const [content, setContent] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-GB';

      recognitionRef.current.onresult = (event: any) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            transcript += event.results[i][0].transcript;
          }
        }
        setContent(prev => prev + (prev ? ' ' : '') + transcript);
      };

      recognitionRef.current.onend = () => setIsListening(false);
    }
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => setSubmitted(false), 300);
    }
  }, [isOpen]);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setIsListening(true);
      recognitionRef.current?.start();
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    setLoading(true);
    try {
      await onSubmit(category, content);
      setSubmitted(true);
      setContent('');
      // Auto close after 2.5 seconds
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err) {
      alert("Feedback could not be sent. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-xl bg-white rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 min-h-[500px] flex flex-col">
        
        {submitted ? (
          <div className="flex-grow flex flex-col items-center justify-center p-12 text-center animate-in zoom-in duration-500">
            <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mb-8 shadow-xl shadow-emerald-50">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="animate-in slide-in-from-bottom-2 duration-700">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Thank You!</h2>
            <p className="text-slate-500 font-medium leading-relaxed max-w-xs">
              Your feedback has been delivered directly to our product engineering team. We appreciate your help in building a better NHSInterview.
            </p>
            <div className="mt-12">
               <div className="w-48 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 animate-[progress_2.5s_ease-in-out]"></div>
               </div>
               <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mt-3">Closing Automatically</p>
            </div>
          </div>
        ) : (
          <>
            <div className="p-10 border-b border-slate-100 flex justify-between items-center shrink-0">
              <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Support & Feedback</h2>
                <p className="text-slate-500 text-xs font-medium uppercase tracking-widest mt-1">Help us improve your prep</p>
              </div>
              <button onClick={onClose} className="p-3 bg-slate-100 text-slate-400 hover:text-slate-900 rounded-full transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-10 space-y-8 flex-grow">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-2">Type of Feedback</label>
                <div className="grid grid-cols-2 gap-3">
                  {['Suggestion', 'Bug', 'Content Request', 'Other'].map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat as any)}
                      className={`py-3 px-4 rounded-2xl text-xs font-black transition-all border-2 ${category === cat ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-slate-50 border-slate-50 text-slate-400 hover:border-slate-200'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-2">Detail</label>
                <textarea
                  required
                  rows={5}
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Tell us what's on your mind... (Use the mic for voice input)"
                  className="w-full p-6 bg-slate-50 border-2 border-slate-50 rounded-3xl outline-none focus:border-blue-500 focus:bg-white font-medium text-slate-700 shadow-inner transition-all resize-none pr-16"
                ></textarea>
                <button
                  type="button"
                  onClick={toggleListening}
                  className={`absolute bottom-6 right-6 p-4 rounded-2xl transition-all ${isListening ? 'bg-red-500 text-white shadow-xl animate-pulse' : 'bg-white border border-slate-200 text-slate-400 hover:text-blue-600 shadow-sm'}`}
                  title="Voice Input"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                </button>
              </div>

              <button
                type="submit"
                disabled={loading || !content.trim()}
                className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-blue-600 disabled:opacity-30 transition-all flex justify-center items-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    Send to Product Team
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="22" y1="2" x2="11" y2="13"/><polyline points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default FeedbackModal;
